import axios from 'axios';
import type { useAuthStore as AuthStoreType } from '../stores/authStore';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Module-level store reference populated lazily on first use.
// This avoids the circular dep: authStore → api → authStore.
let _authStore: typeof AuthStoreType | null = null;
async function authStore() {
  if (!_authStore) {
    _authStore = (await import('../stores/authStore')).useAuthStore;
  }
  return _authStore;
}

// Attach access token + tenant slug on every request.
// The interceptor is async-safe: Axios awaits the returned promise.
api.interceptors.request.use(async config => {
  const store = await authStore();
  const { accessToken, tenant } = store.getState();
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  if (tenant?.slug) config.headers['X-Tenant-Slug'] = tenant.slug;
  return config;
});

// On 401: attempt a single token refresh then replay the original request.
// Refresh calls themselves are excluded — they're handled by RequireAuth in the Router.
let refreshPromise: Promise<string> | null = null;

api.interceptors.response.use(
  r => r,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);
    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    const isRefreshCall = original?.url?.includes('/auth/refresh');
    if (error.response?.status === 401 && original && !original._retry && !isRefreshCall) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post('/auth/refresh')
            .then(res => res.data.data.access_token as string)
            .finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        const store = await authStore();
        store.getState().setAccessToken(newToken);
        if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        const store = await authStore();
        store.getState().clearAuth();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export function formatUGX(amount: number): string {
  return 'UGX ' + Math.round(amount).toLocaleString('en-UG');
}

export function apiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message ?? err.message;
  }
  return 'An unexpected error occurred';
}
